import {
  parseDateToString,
  parseServiceDates,
} from '@rover/react-lib/src/pages/search/SearchPage/utilities/parseDate';
import type { DataLayerEvent, SearchFilters } from '@rover/types';
import { ServiceTypeSlugs } from '@rover/types/src/Service';

import { getDogSizes, getLocationType } from './utilities';

type AnalyticsDataType = Record<string, string | string[] | null | number | undefined | boolean>;

const getExtendedData = (query: Partial<SearchFilters>): AnalyticsDataType =>
  query
    ? {
        priceRangeMin: query.minprice || null,
        priceRangeMax: query.maxprice || null,
        numberOfDogs: query.pet?.length || null,
        numberOfVisitsPerDay:
          query.serviceType === ServiceTypeSlugs.dropIn ? query.spacesRequired : null,
        serviceDays: query.selectedDays?.length ? parseServiceDates(query.selectedDays) : null,
        dropOffDate: query.startDate ? parseDateToString(query.startDate) : null,
        pickUpDate: query.endDate ? parseDateToString(query.endDate) : null,
        frequency: query.frequency || null,
        spacesRequired: query.spacesRequired || null,
        postalCode: query.location || null,
        morningAvailability: query.morningAvailability || null,
        middayAvailability: query.middayAvailability || null,
        eveningAvailability: query.eveningAvailability || null,
        fulltimeAvailability: query.fulltimeAvailability || null,
        breeds: query.breeds?.length ? query.breeds.join(', ') : null,
      }
    : {};

const getQueryData = (query: Partial<SearchFilters>) =>
  query
    ? {
        dogSize: getDogSizes(query),
        dropOffDate: query.startDate || null,
        pickUpDate: query.endDate || null,
        serviceType: query.serviceType || null,
        zipCode: query.location || null,
      }
    : {};

const events = {
  QuickMatchSearchResultsDisplayEvent: {
    event: 'quick-match-search-results-display',
  },
  SearchOwnerPreferencesDisplayEvent: {
    event: 'search-owner-preferences-display-search-wizard',
    nonInteractive: true,
  },
  SearchOwnerPreferencesCloseEvent: {
    event: 'search-owner-preferences-close',
  },
  PerformSearchEvent: (query: Partial<SearchFilters>): DataLayerEvent => ({
    event: 'perform-search',
    dropOffDate: query.startDate || null,
    pickUpDate: query.endDate || null,
    serviceType: query.serviceType || null,
    zipCode: query.location || null,
  }),
  SearchMapUpdateWhenIMoveMapCheckboxClickEvent: (checked: boolean): DataLayerEvent => ({
    event: 'search-map-update-when-i-move-map-checkbox',
    checked,
  }),
  SearchMapEnlargeOrShrinkMapClickEvent: (action: string): DataLayerEvent => ({
    event: 'search-map-enlarge-or-shrink-map',
    action,
  }),
  LocationTypeSearchEvent: (query: Partial<SearchFilters>): DataLayerEvent => {
    const data = query && {
      search_centerlat: query.centerlat,
      search_centerlng: query.centerlng,
      search_dog_preferences: query.dogPreference,
      search_end_date: query.endDate,
      search_location: query.location,
      search_maxlat: query.maxlat,
      search_maxlng: query.maxlng,
      search_maxprice: query.maxprice,
      search_minlat: query.minlat,
      search_minlng: query.minlng,
      search_minprice: query.minprice,
      search_page: query.page,
      search_service_type: query.serviceType,
      search_start_date: query.startDate,
      search_zoomlevel: query.zoomlevel,
      service_type: query.serviceType,
    };
    const location = query && query.location;
    return {
      event: `${getLocationType(location)}-search`,
      ...data,
    };
  },
  SearchSearchPageVisitEvent: (query: Partial<SearchFilters>): DataLayerEvent => ({
    event: 'search-search-page-visit',
    ...getQueryData(query),
  }),

  /* The search visit event was consuming too much CPU, so we
     separated it into two events to allow the main thread to
     respond to user input. */
  SearchSearchPageVisitGaEvent: (query: Partial<SearchFilters>): DataLayerEvent => ({
    event: 'search-search-page-visit-ga',
    ...getQueryData(query),
  }),
  SearchFiltersClickEvent: (query: Partial<SearchFilters>): DataLayerEvent => ({
    event: 'search-mobile-filter-click',
    ...getQueryData(query),
  }),
  SearchPageCalendarClick: (filters: Partial<SearchFilters>): DataLayerEvent => ({
    event: 'search-page-calendar-click',
    ...getQueryData(filters),
    ...getExtendedData(filters),
  }),
  SearchPageSearchCTA: (filters: Partial<SearchFilters>): DataLayerEvent => ({
    event: 'search-page-search-cta',
    ...getQueryData(filters),
    ...getExtendedData(filters),
  }),
};

export default events;
